import { create } from 'zustand';
import type { ConversazioneSalvata, EsitoTurno, ParteRisposta, TipoAllegato, TurnoSalvato } from '@masamune/shared';

/**
 * Il pannello dell'assistente (components/layout/AssistentePanel.tsx): aperto o chiuso,
 * cosa mostra (la chat, il foglio «Cosa sa MUNE» di un cliente o progetto, le
 * conversazioni salvate, «Cosa ha fatto MUNE») e la conversazione in corso. Resta aperto
 * mentre si cambia pagina: è quello che serve per chiedere «e questo cliente?» guardando la
 * sua scheda.
 *
 * Dal 29/10/2026 (fetta 2B della Fase 2) la conversazione la salva il server: qui c'è il suo
 * id (`conversazioneId`) e la risposta arriva a parti, in streaming (chatApi.ts).
 */

/** Una scheda memoria letta dall'Assistente (evento `letto` della chat): la riga «Letto prima di rispondere». */
export interface SchedaLetta {
  tipo: 'cliente' | 'progetto';
  id: string;
  nome: string;
  righe: number;
}

/**
 * Una foto o un PDF nella chat (fetta 2F, 29/10/2026, os-planning/ai/fase-2f-allegati.md): un pezzo
 * della striscia sopra il campo (features/assistente/allegati.ts, useAllegatiCampo) o del fumetto
 * della domanda. All'invio i pezzi passano dalla striscia alla domanda con gli stessi indirizzi
 * blob:, e da lì li libera la chat quando butta i messaggi (nuovaConversazione, caricaConversazione);
 * se la domanda torna nel campo, tornano nella striscia così come sono (togliMessaggi).
 */
export interface AllegatoInChat {
  /** Locale, per React. */
  chiave: string;
  /** La riga di AiAttachment; null finché il file non è sul server. */
  id: string | null;
  tipo: TipoAllegato;
  nome: string;
  pagine: number | null;
  /** blob: della foto rimpicciolita, o data: della miniatura di una conversazione riaperta; null per i PDF. */
  anteprima: string | null;
  /** Da 0 a 1 mentre si carica, poi null. */
  avanzamento: number | null;
  /** false: MUNE non l'ha letto ed è uscito dalla conversazione («Non letto»). */
  letto: boolean;
}

export interface MessaggioChat {
  id: string;
  ruolo: 'utente' | 'assistente';
  /** La domanda; per una risposta che non è arrivata, la frase dell'errore (le risposte vere stanno in `parti`). */
  testo: string;
  /** La risposta non è arrivata: il fumetto rosso. */
  errore?: boolean;
  /** Risposta della modalità finta (senza chiave). */
  finta?: boolean;
  /** Le schede lette prima di rispondere: la riga «Letto prima di rispondere». */
  lette?: SchedaLetta[];
  /** Risposta appena arrivata: compare da sola, man mano (AssistentePanel e rivelaFlusso.ts). */
  daRivelare?: boolean;
  /** Sul messaggio dell'utente: la reazione dell'Assistente, come su WhatsApp (👍, ❤️, …). */
  reazione?: string;
  // ── Fase 2, la chat in streaming (29/10/2026) ──
  /** L'id della riga della domanda sul server (`domandaId` dell'evento `conversazione`): sulla domanda e sulla sua risposta. */
  serverId?: string;
  /** Le parti della risposta nell'ordine in cui arrivano: testo, riquadri, righe con link, avvisi. */
  parti?: ParteRisposta[];
  /** Il flusso della risposta è ancora aperto: possono arrivare altre parti. */
  inArrivo?: boolean;
  /**
   * La frase della riga di stato mandata dal server («Controllo Qonto…»). Null finché non ne
   * arriva una («Sto pensando…») e di nuovo quando arriva il testo: la riga sparisce e torna
   * col prossimo `stato`.
   */
  stato?: string | null;
  /** Com'è finito il turno (evento `fine`, o l'errore che l'ha fermato). */
  esito?: EsitoTurno;
  /**
   * Sulla domanda (2F, 29/10/2026): le foto e i PDF mandati, nel fumetto sopra il testo. Mentre
   * si caricano ancora (Invia premuto prima della fine) li aggiorna il campo, con la barra.
   */
  allegati?: AllegatoInChat[];
}

/** Di chi è il foglio «Cosa sa MUNE» aperto nel pannello. */
export interface BersaglioMemoria {
  tipo: 'cliente' | 'progetto';
  id: string;
  nome: string;
}

/** Le viste del pannello: la chat resta montata (nascosta) mentre se ne guarda un'altra. */
export type VistaPannello = 'chat' | 'memoria' | 'conversazioni' | 'azioni';

interface AssistenteState {
  aperto: boolean;
  apri: () => void;
  chiudi: () => void;
  alterna: () => void;
  /**
   * MUNE a schermo intero, solo al computer (Lorenzo, 30/10/2026: «una versione a schermo intero,
   * per comodità, come chatgpt»): le conversazioni a sinistra e la chat al centro. La scelta resta
   * chiudendo e riaprendo, anche dopo un ricaricamento; al telefono il pannello copre già tutto.
   */
  intero: boolean;
  alternaIntero: () => void;
  esciDaIntero: () => void;
  vista: VistaPannello;
  apriVista: (v: VistaPannello) => void;
  memoria: BersaglioMemoria | null;
  /** Apre il pannello sul foglio «Cosa sa MUNE» di un cliente o progetto. */
  apriMemoria: (b: BersaglioMemoria) => void;
  tornaAllaChat: () => void;
  messaggi: MessaggioChat[];
  /** Aggiunge un messaggio e ne restituisce l'id. */
  aggiungi: (m: Omit<MessaggioChat, 'id'>) => string;
  aggiornaMessaggio: (id: string, patch: Partial<Omit<MessaggioChat, 'id' | 'ruolo'>>) => void;
  /**
   * Toglie dei messaggi (2F, 29/10/2026): la domanda che torna nel campo e la sua risposta mai
   * arrivata. Non libera gli indirizzi blob: delle foto, che tornano nella striscia.
   */
  togliMessaggi: (ids: string[]) => void;
  /** Una parte nuova della risposta; un testo che segue un testo si unisce a lui. */
  aggiungiParte: (id: string, parte: ParteRisposta) => void;
  /** La risposta ha finito di comparire: se il pannello si riapre non riparte da capo. */
  segnaRivelato: (id: string) => void;
  /** L'Assistente reagisce a un messaggio dell'utente. */
  reagisci: (id: string, reazione: string) => void;
  /** La conversazione salvata sul server (evento `conversazione`); null finché non c'è. */
  conversazioneId: string | null;
  setConversazioneId: (id: string | null) => void;
  /**
   * Svuota la chat e ne comincia una nuova. Il flusso in corso lo interrompe il pannello
   * (cambia `conversazione`), così la domanda subito dopo non prende 409. Libera gli indirizzi
   * blob: delle foto nei messaggi che butta (2F).
   */
  nuovaConversazione: () => void;
  /** Apre nella chat una conversazione salvata, da continuare (ConversazioniVista). Come sopra per le foto. */
  caricaConversazione: (c: ConversazioneSalvata) => void;
  /**
   * Quale conversazione: cresce a ogni «Nuova conversazione» e a ogni conversazione aperta.
   * Il pannello interrompe il flusso della conversazione di prima e butta quello che arriva tardi.
   */
  conversazione: number;
  /** Un turno è in corso: dalla domanda alla fine della risposta (la mascotte pensa). */
  inAttesa: boolean;
  setInAttesa: (b: boolean) => void;
  /**
   * Il testo da mettere nel campo della chat: «Chiedi a MUNE: …» della ricerca senza
   * risultati (29/10/2026). Il pannello lo prende, lo mette nel campo e lo svuota: non
   * manda niente, la chat parte solo da Invio.
   */
  bozza: string | null;
  chiediAMune: (testo: string) => void;
  svuotaBozza: () => void;
}

let n = 0;
const nuovoId = () => `m${Date.now().toString(36)}${(n++).toString(36)}`;

/**
 * Un turno salvato come lo mostra la chat: la domanda (con la reazione) e la risposta, già
 * tutta visibile. Una domanda senza risposta salvata dice «Risposta interrotta: riprova»
 * («Parole a schermo»): il server si è fermato prima di chiudere il turno.
 *
 * Dalla 2F (29/10/2026) la domanda porta le sue foto e i suoi PDF, con le miniature data: che
 * manda il server: una foto senza miniatura (la sua riga non c'è più) resta col nome e l'icona.
 */
function messaggiDelTurno(t: TurnoSalvato): MessaggioChat[] {
  const r = t.risposta;
  const allegati = (t.domanda.allegati ?? []).map((a, i): AllegatoInChat => ({
    // Unica in tutta la chat: un doppione ha l'id della riga che c'era già, e lo stesso id può
    // tornare nel fumetto di un'altra domanda.
    chiave: `${t.domanda.id}-${i}`,
    id: a.id,
    tipo: a.tipo,
    nome: a.nome,
    pagine: a.pagine,
    anteprima: a.miniatura,
    avanzamento: null,
    letto: a.letto,
  }));
  const domanda: MessaggioChat = {
    id: nuovoId(),
    ruolo: 'utente',
    testo: t.domanda.testo,
    serverId: t.domanda.id,
    ...(r?.reazione ? { reazione: r.reazione } : {}),
    ...(allegati.length ? { allegati } : {}),
  };
  const risposta: MessaggioChat = {
    id: nuovoId(),
    ruolo: 'assistente',
    testo: '',
    serverId: t.domanda.id,
    parti: r ? r.parti : [{ tipo: 'avviso', testo: 'Risposta interrotta: riprova', tono: 'alert' }],
    lette: r?.lette ?? [],
    finta: r?.finta ?? false,
    esito: r?.esito ?? 'interrotto',
  };
  return [domanda, risposta];
}

/**
 * Gli indirizzi blob: delle foto nei fumetti delle domande che la chat butta (2F, 29/10/2026): li
 * ha creati il campo (allegati.ts) e all'invio sono passati alla domanda, che da lì li tiene. Le
 * miniature data: di una conversazione riaperta non hanno niente da liberare.
 */
function liberaIndirizzi(messaggi: MessaggioChat[]) {
  for (const m of messaggi) {
    for (const a of m.allegati ?? []) {
      if (a.anteprima?.startsWith('blob:')) URL.revokeObjectURL(a.anteprima);
    }
  }
}

/** Lo schermo intero scelto, per questo browser: un'impostazione di comodo, se il browser non la tiene si riparte dal pannello. */
const CHIAVE_INTERO = 'msm-mune-intero';
function interoScelto(): boolean {
  try {
    return localStorage.getItem(CHIAVE_INTERO) === '1';
  } catch {
    return false;
  }
}
function ricordaIntero(intero: boolean): void {
  try {
    if (intero) localStorage.setItem(CHIAVE_INTERO, '1');
    else localStorage.removeItem(CHIAVE_INTERO);
  } catch {
    /* navigazione privata o archivio pieno: vale fino al ricaricamento */
  }
}

export const useAssistenteStore = create<AssistenteState>()((set, get) => ({
  aperto: false,
  apri: () => set({ aperto: true }),
  chiudi: () => set({ aperto: false }),
  alterna: () => set((s) => ({ aperto: !s.aperto })),
  intero: interoScelto(),
  // A schermo intero le conversazioni stanno nella barra a sinistra: la loro vista torna la chat.
  alternaIntero: () => set((s) => {
    const intero = !s.intero;
    ricordaIntero(intero);
    return { intero, vista: intero && s.vista === 'conversazioni' ? 'chat' : s.vista };
  }),
  esciDaIntero: () => {
    ricordaIntero(false);
    set({ intero: false });
  },
  vista: 'chat',
  apriVista: (vista) => set({ vista }),
  memoria: null,
  apriMemoria: (memoria) => set({ aperto: true, vista: 'memoria', memoria }),
  tornaAllaChat: () => set({ vista: 'chat' }),
  messaggi: [],
  aggiungi: (m) => {
    const id = nuovoId();
    set((s) => ({ messaggi: [...s.messaggi, { ...m, id }] }));
    return id;
  },
  aggiornaMessaggio: (id, patch) =>
    set((s) => (s.messaggi.some((m) => m.id === id)
      ? { messaggi: s.messaggi.map((m) => (m.id === id ? { ...m, ...patch } : m)) }
      : s)),
  togliMessaggi: (ids) =>
    set((s) => {
      const via = new Set(ids);
      return s.messaggi.some((m) => via.has(m.id)) ? { messaggi: s.messaggi.filter((m) => !via.has(m.id)) } : s;
    }),
  aggiungiParte: (id, parte) =>
    set((s) => ({
      messaggi: s.messaggi.map((m) => {
        if (m.id !== id) return m;
        const parti = m.parti ?? [];
        const ultima = parti[parti.length - 1];
        // Il testo arriva a frasi: una frase dopo l'altra è lo stesso paragrafo, e le parole già
        // comparse restano dove sono (rivelaFlusso.ts conta i caratteri dall'inizio della risposta).
        // Lo spazio o l'a capo tra due frasi il server lo mette in testa alla frase dopo, così il
        // testo non finisce mai con un a capo e la mascotte resta dopo l'ultima parola.
        if (parte.tipo === 'testo' && ultima?.tipo === 'testo') {
          return { ...m, parti: [...parti.slice(0, -1), { tipo: 'testo', testo: ultima.testo + parte.testo }] };
        }
        // Un testo che comincia un blocco (in cima, o dopo un riquadro) non comincia con lo
        // spazio della frase di prima: il blocco è pre-wrap e sarebbe una riga vuota (29/10/2026).
        if (parte.tipo === 'testo') {
          const testo = parte.testo.replace(/^\s+/, '');
          return testo ? { ...m, parti: [...parti, { tipo: 'testo', testo }] } : m;
        }
        return { ...m, parti: [...parti, parte] };
      }),
    })),
  segnaRivelato: (id) =>
    set((s) => (s.messaggi.some((m) => m.id === id && m.daRivelare)
      ? { messaggi: s.messaggi.map((m) => (m.id === id ? { ...m, daRivelare: false } : m)) }
      : s)),
  reagisci: (id, reazione) => set((s) => ({ messaggi: s.messaggi.map((m) => (m.id === id ? { ...m, reazione } : m)) })),
  conversazioneId: null,
  setConversazioneId: (conversazioneId) => set({ conversazioneId }),
  nuovaConversazione: () => {
    liberaIndirizzi(get().messaggi);
    set((s) => ({ messaggi: [], inAttesa: false, conversazione: s.conversazione + 1, conversazioneId: null }));
  },
  caricaConversazione: (c) => {
    liberaIndirizzi(get().messaggi);
    set((s) => ({
      messaggi: c.turni.flatMap(messaggiDelTurno),
      conversazioneId: c.id,
      vista: 'chat',
      inAttesa: false,
      conversazione: s.conversazione + 1,
    }));
  },
  conversazione: 0,
  inAttesa: false,
  setInAttesa: (inAttesa) => set({ inAttesa }),
  bozza: null,
  chiediAMune: (bozza) => set({ aperto: true, vista: 'chat', bozza }),
  svuotaBozza: () => set({ bozza: null }),
}));
